import { FlexCenter } from "@9amhealth/shared";
import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import { NineNavigation } from "@9amhealth/wcl/generated/react";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import asTemplateValue from "lib/asTemplateValue";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { TOP_NAV_HEIGHT } from "src/constants/layout";
import { PayerId } from "src/constants/payers";
import useSubscription from "src/lib/useSubscription";
import SignupCubit from "src/state/SignupCubit/SignupCubit";
import type {
  SignupCustomPageParameters,
  SignupCustomStep
} from "src/ui/components/SignupCustomContent/SignupCustom.types";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import { campaignInstances } from "src/ui/components/SignupCustomContent/campaigns/SignupCustomCampaignGenericInstances";

const Table = styled.table`
  border: 1px solid black;
  margin: 2rem auto 6rem;
  max-width: calc(100vw - 2em);

  th {
    border: 1px solid black;
    padding: 0.5rem;
    background: #eee;
  }
  td {
    border: 1px solid black;
    padding: 0.5rem;

    a {
      text-decoration: underline;
    }
  }
`;

const PageWrap = styled.div`
  padding-top: ${TOP_NAV_HEIGHT};
  height: 100vh;
  overflow: auto;
`;

const CenteredContent = styled(FlexCenter)`
  display: contents;
`;

const CheckDataCompleted: FC<{ step: SignupCustomStep }> = ({ step }) => {
  const [completed, setCompleted] = useState<string>("⏳");

  useEffect(() => {
    if (step.dataComplete && typeof step.dataComplete === "function") {
      const c = step.dataComplete();
      if (typeof c === "boolean") {
        setCompleted(c ? "✅" : "❌");
      } else if (c instanceof Promise) {
        c.then((d) => {
          setCompleted(d ? "✅" : "❌");
        }).catch(() => {
          setCompleted("⛔️");
        });
      }
    } else {
      setCompleted("🤞");
    }
  }, []);

  return <b>{completed}</b>;
};

const SignupCustomPageDebug: FC = () => {
  useEffect(() => {
    SignupCubit.loadSupportChat();
    return SignupCubit.removeSupportChat;
  }, []);

  const parameters = useParams<SignupCustomPageParameters>();
  const sub = useSubscription({
    status: [
      SubscriptionDetailsResponse.status.DRAFT,
      SubscriptionDetailsResponse.status.ACTIVE
    ],
    metadataPartnerFunnel: [parameters.campaign ?? ""]
  });
  const [state, { coBrandedLogo, campaignSteps, campaignPrettyName }] =
    useBlocNext(SignupCustomBloc, {
      props: {
        parameters: {
          campaign: parameters.campaign,
          step: undefined
        },
        options: {
          preview: true
        }
      }
    });

  // Use subscription's funnel field to check if it matches the campaign
  const funnelMatches = sub?.funnel === parameters.campaign;

  // Find any purchase item for payer info (we still need payer from metadata)
  const partnerItem = funnelMatches
    ? sub?.purchaseItems.find((item) => Boolean(item.metadata["partner.payer"]))
    : undefined;

  const payer = asTemplateValue(partnerItem?.metadata["partner.payer"]);

  const subDetails = {
    totalPrice: sub?.totalPrice,
    coveredBy: payer ? String(payer) : null,
    cashPay: payer === PayerId.CASH_PAY ? "Yes" : "No"
  };

  return (
    <PageWrap>
      <NineNavigation
        app
        variant="stepper"
        style={{ background: `var(--color-cream)` }}
        partnerLogo={coBrandedLogo ? coBrandedLogo + "?mock-type=.png" : ""}
      >
        <nine-overlay slot="help" variant="toast">
          <div slot="trigger" className="strong">
            Help?
          </div>
          <div slot="content">
            <h5 className="strong m0">Questions?</h5>
            <p className="lauf-text">
              Text or call us at <a href="tel:2029329958">(202) 932-9958</a>
              <br />
              or email us anytime at{" "}
              <a href="mailto:support@join9am.com">support@join9am.com</a>
            </p>
          </div>
        </nine-overlay>
      </NineNavigation>

      <nine-spacer s="xxl"></nine-spacer>
      <CenteredContent>
        <h4>Campaign: {campaignPrettyName}</h4>
      </CenteredContent>
      {sub && (
        <Table>
          <thead>
            <tr>
              <th>Subscription Key</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>TotalPrice</td>
              <td>{subDetails.totalPrice}</td>
            </tr>

            <tr>
              <td>CoveredBy</td>
              <td>{subDetails.coveredBy}</td>
            </tr>

            <tr>
              <td>CashPay</td>
              <td>{subDetails.cashPay}</td>
            </tr>
          </tbody>
        </Table>
      )}
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Path</th>
            <th>Completed</th>
          </tr>
        </thead>
        <tbody>
          {campaignSteps?.map((step, i) => (
            <tr key={`${i}`}>
              <td>{step.name}</td>
              <td>{step.type}</td>
              <td>
                <a
                  href={`/signup/${state.campaign}/${step.path}?preview=true`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {step.path}
                </a>
              </td>
              <td>
                <CheckDataCompleted step={step} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <CenteredContent>
        <h4 style={{ margin: 0 }}>Custom Campaigns</h4>
      </CenteredContent>
      <Table>
        <thead>
          <tr>
            <th>Funnel Name</th>
            <th>Funnel Path</th>
          </tr>
        </thead>
        <tbody>
          {campaignInstances().map((campaign, i) => {
            return (
              <tr key={`${i}`}>
                <td>{campaign.prettyName}</td>
                <td>
                  <a href={`/debug/signup/${campaign.name}`}>{campaign.name}</a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </PageWrap>
  );
};

export default SignupCustomPageDebug;
